import { useEffect, useMemo } from 'react';

import { usePxwSelectionInputs } from './usePxwSelectionInputs';
import { usePxwSelectionOptions } from './usePxwSelectionOptions';

import { IGeneratedPayload } from '../../views/PxwProcessDevices/types';

export const usePxwSelectionForm = (
  selectedTenant: string | null,
  isParentLoading: boolean,
  onSynthesize: (payload: IGeneratedPayload) => void,
  onResultInvalidated: () => void
) => {
  const options = usePxwSelectionOptions(selectedTenant);
  const {
    inputs,
    reset,
    updateQty,
    handleTestProviderChange,
    handlePOChange,
    handleItemChange,
  } = usePxwSelectionInputs(onResultInvalidated);

  useEffect(() => {
    reset();
  }, [selectedTenant]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalQty = useMemo(() => {
    const { matchQty, mismatchFunctionalQty, mismatchNonFunctionalQty } = inputs;
    return (
      (matchQty === '' ? 0 : matchQty) +
      (mismatchFunctionalQty === '' ? 0 : mismatchFunctionalQty) +
      (mismatchNonFunctionalQty === '' ? 0 : mismatchNonFunctionalQty)
    );
  }, [inputs]);

  const receivedQty = inputs.selectedItem?.quantityInProcessingSourceBins ?? -1;

  const isSubmitDisabled =
    isParentLoading ||
    !selectedTenant ||
    !inputs.selectedTestProvider ||
    !inputs.selectedSource ||
    !inputs.selectedItem ||
    inputs.matchQty === '' ||
    inputs.mismatchFunctionalQty === '' ||
    inputs.mismatchNonFunctionalQty === '' ||
    totalQty !== receivedQty;

  const showValidationHint =
    !!inputs.selectedItem &&
    (inputs.matchQty !== '' ||
      inputs.mismatchFunctionalQty !== '' ||
      inputs.mismatchNonFunctionalQty !== '');

  const handleSubmit = () => {
    onSynthesize({
      sourceType: inputs.selectedSource!.sourceType,
      sourceNumber: inputs.selectedSource!.sourceNumber,
      itemNumber: inputs.selectedItem!.itemNumber,
      provider: inputs.selectedTestProvider!.testProviderId,
      matchCount: inputs.matchQty as number,
      mismatchFunctionalCount: inputs.mismatchFunctionalQty as number,
      mismatchNonfunctionalCount: inputs.mismatchNonFunctionalQty as number,
      selectedItem: inputs.selectedItem!,
    });
  };

  return {
    formOptions: options,
    formInputs: inputs,
    updateQty,
    totalQty,
    receivedQty,
    isSubmitDisabled,
    showValidationHint,
    handleTestProviderChange,
    handlePOChange,
    handleItemChange,
    handleSubmit,
  };
};
